/**
 * Device-compass heading (magnetometer), exposed as a tiny reactive proxy.
 *
 * GPS `heading` (GeoFix.heading) is course-over-ground — the direction you're
 * *travelling*, which is meaningless while standing still. The HUD's
 * North/Center pointer needs the direction the phone is physically pointing,
 * which only the device compass provides, via the DeviceOrientation API.
 *
 *   • `$compass.heading` — degrees clockwise from true north [0,360), or null
 *     until a reading arrives (or if the device has no magnetometer / the user
 *     denied the sensor). Consumers fall back to GPS heading when it's null.
 *
 * iOS 13+ gates orientation behind a permission prompt that must be requested
 * from a user gesture, so `initCompass()` is called both at startup (enough for
 * Android / desktop) and from the first user tap (for the iOS prompt).
 */
import A from "aberdeen";

export const $compass = A.proxy({ heading: null as number | null });

let listening = false;
let lastEmitMs = 0;

/** Current screen rotation in degrees, to correct the raw alpha reading. */
function screenAngle(): number {
  const so = (screen as Screen & { orientation?: { angle?: number } }).orientation;
  if (so && typeof so.angle === "number") return so.angle;
  return (window as Window & { orientation?: number }).orientation ?? 0;
}

function onOrientation(e: DeviceOrientationEvent): void {
  let heading: number | null = null;
  const wk = (e as DeviceOrientationEvent & { webkitCompassHeading?: number })
    .webkitCompassHeading;
  if (typeof wk === "number" && !Number.isNaN(wk)) {
    // iOS: already degrees clockwise from north, for the top of the device.
    heading = wk;
  } else if (e.absolute && e.alpha != null) {
    // Standard: alpha is counter-clockwise from north; flip and correct for the
    // current screen rotation.
    heading = 360 - e.alpha + screenAngle();
  }
  if (heading == null) return;

  // Throttle to ~10 Hz and quantise to whole degrees, so we don't re-render the
  // HUD on every 60 Hz sensor tick.
  const now = Date.now();
  if (now - lastEmitMs < 100) return;
  lastEmitMs = now;
  const next = Math.round(((heading % 360) + 360) % 360);
  if (next !== $compass.heading) $compass.heading = next;
}

function startListening(): void {
  if (listening) return;
  listening = true;
  window.addEventListener("deviceorientationabsolute", onOrientation as EventListener);
  window.addEventListener("deviceorientation", onOrientation as EventListener);
}

/** Begin compass updates. Safe to call multiple times. */
export function initCompass(): void {
  const req = (
    DeviceOrientationEvent as unknown as {
      requestPermission?: () => Promise<"granted" | "denied">;
    }
  )?.requestPermission;
  if (typeof req === "function") {
    // iOS — must run inside a user gesture; rejects otherwise (caught).
    req().then((state) => {
      if (state === "granted") startListening();
    }).catch(() => {});
  } else {
    startListening();
  }
}
